import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';
import { C, cs } from '../styles/common';
import PartDetail from '../components/PartDetail';
import { RecipeCard } from '../components/RecipeCard';

export default function Parts() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('parts');
  const [parts, setParts]         = useState([]);
  const [products, setProducts]   = useState([]);
  const [recipes, setRecipes]     = useState([]);
  const [colours, setColours]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);
  const [search, setSearch]       = useState('');
  const [recipeSearch, setRecipeSearch] = useState('');
  const [showSugg, setShowSugg]   = useState(false);
  const [sortBy, setSortBy]       = useState('name');
  const [showDiscontinued, setShowDiscontinued] = useState(searchParams.get('discontinued') === 'show');
  const [showProductParts, setShowProductParts] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [recipeModalPart, setRecipeModalPart]   = useState(null);

  useEffect(() => { loadAll(); }, []);

  function loadAll() {
    setLoading(true);
    Promise.all([
      api.get('/api/v1/parts?all=true'),
      api.get('/api/v1/products').catch(() => ({ data: [] })),
      api.get('/api/v1/recipes').catch(() => ({ data: [] })),
      api.get('/api/v1/colours').catch(() => ({ data: [] })),
    ]).then(([p, pr, r, co]) => {
      setParts(p.data);
      setProducts(pr.data);
      setRecipes(r.data);
      setColours(co.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  function toggleProduct(id) {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function sortKey(type, data) {
    switch (sortBy) {
      case 'number':   return type === 'product' ? (data.code || '') : (data.part_number || data.name || '');
      case 'mould':    return type === 'product' ? (data.product_parts?.[0]?.part?.mould?.name || data.product_parts?.[0]?.part?.mould?.ref || '') : (data.mould?.name || data.mould?.ref || '');
      case 'machine':  return type === 'product' ? (data.product_parts?.[0]?.part?.primary_machine?.display_name || data.product_parts?.[0]?.part?.primary_machine?.name || '') : (data.primary_machine?.display_name || data.primary_machine?.name || '');
      default:         return type === 'product' ? (data.name || '') : (data.name || '');
    }
  }

  const suggestions = search.length < 1 ? [] : (() => {
    const q = search.toLowerCase();
    const results = [];
    for (const p of products) {
      if ((p.name||'').toLowerCase().includes(q) || (p.code||'').toLowerCase().includes(q))
        results.push({ type: 'product', id: p.id, name: p.name, sub: p.code });
    }
    for (const p of parts) {
      if ((p.name||'').toLowerCase().includes(q) || (p.part_number||'').toLowerCase().includes(q) || (p.customer||'').toLowerCase().includes(q))
        results.push({ type: 'part', id: p.id, name: p.name, sub: p.part_number });
    }
    return results.slice(0, 8);
  })();

  if (selected) return (
    <PartDetail
      partId={selected}
      scope="worker"
      onBack={() => { setSelected(null); loadAll(); }}
    />
  );

  const filteredRecipes = recipes.filter(r =>
    !recipeSearch || r.name.toLowerCase().includes(recipeSearch.toLowerCase()) ||
    (r.material?.name || '').toLowerCase().includes(recipeSearch.toLowerCase())
  );

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Parts &amp; Recipes</h1>
          <p style={s.sub}>{parts.length} parts · {recipes.length} recipes</p>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: '20px' }}>
        {[['parts', 'fa-solid fa-cube', 'Parts'], ['recipes', 'fa-solid fa-flask', 'Recipes']].map(([key, icon, label]) => (
          <button key={key} onClick={() => setActiveTab(key)}
            style={{ background: 'none', border: 'none', borderBottom: activeTab === key ? `2px solid ${C.teal}` : '2px solid transparent',
              color: activeTab === key ? C.text : C.muted, padding: '8px 18px', fontSize: '13px', fontWeight: '600',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'system-ui, sans-serif' }}>
            <i className={icon} style={{ fontSize: '11px' }} />{label}
          </button>
        ))}
      </div>

      {/* ── Recipes tab ─────────────────────────────────────── */}
      {activeTab === 'recipes' && (
        <div>
          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: C.muted, fontSize: '12px', pointerEvents: 'none' }} />
            <input
              style={{ ...s.searchInput, paddingLeft: '34px' }}
              placeholder="Search recipes or materials…"
              value={recipeSearch}
              onChange={e => setRecipeSearch(e.target.value)}
            />
            {recipeSearch && <button style={s.clearBtn} onClick={() => setRecipeSearch('')}>✕</button>}
          </div>
          {loading ? (
            <div style={s.center}>Loading…</div>
          ) : filteredRecipes.length === 0 ? (
            <div style={{ textAlign: 'center', color: C.muted, fontSize: '13px', padding: '40px 0' }}>
              {recipeSearch ? `No recipes matching "${recipeSearch}"` : 'No recipes yet'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
              {filteredRecipes.map(r => (
                <RecipeCard key={r.id} recipe={r} colours={colours} viewMode="pct" />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Parts tab ────────────────────────────────────────── */}
      {activeTab === 'parts' && (<div>
      <div style={{ position: 'relative', marginBottom: '16px' }}>
        <div style={s.searchWrap}>
          <i className="fa-solid fa-magnifying-glass" style={s.searchIcon} />
          <input
            style={s.searchInput}
            placeholder="Search by name or part number…"
            value={search}
            onChange={e => { setSearch(e.target.value); setShowSugg(true); }}
            onFocus={() => setShowSugg(true)}
            onBlur={() => setTimeout(() => setShowSugg(false), 150)}
          />
          {search && <button style={s.clearBtn} onMouseDown={() => setSearch('')}>✕</button>}
        </div>
        {search && showSugg && suggestions.length > 0 && (
          <div style={s.suggestions}>
            {suggestions.map((item, i) => (
              <div key={i} style={s.suggItem}
                onMouseDown={() => {
                  if (item.type === 'part') { setSelected(item.id); setSearch(''); }
                  else { toggleProduct(item.id); setSearch(''); setShowSugg(false); }
                }}>
                <i className={`fa-solid fa-${item.type === 'product' ? 'layer-group' : 'cube'}`}
                  style={{ fontSize: '11px', color: C.teal, marginRight: '10px', flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: '600' }}>{item.name}</div>
                  {item.sub && <div style={{ fontSize: '10px', color: C.teal, fontFamily: 'monospace' }}>{item.sub}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading ? (
        <div style={s.center}>Loading...</div>
      ) : (
        <>
          {/* Sort + filter bar */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '11px', color: C.muted, marginRight: '4px' }}>Sort by</span>
            {[['name','Name'],['number','Number'],['mould','Mould'],['machine','Machine']].map(([k,l]) => (
              <button key={k} onClick={() => setSortBy(k)}
                style={{ background: sortBy === k ? C.teal : 'none', color: sortBy === k ? '#fff' : '#9ca3af', border: `1px solid ${sortBy === k ? C.teal : C.border}`, borderRadius: '6px', padding: '4px 10px', fontSize: '11px', cursor: 'pointer', fontWeight: sortBy === k ? '700' : '400' }}>
                {l}
              </button>
            ))}
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowDiscontinued(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: showDiscontinued ? '#78350f' : 'none',
                  color: showDiscontinued ? '#fcd34d' : C.muted,
                  border: `1px solid ${showDiscontinued ? '#b45309' : C.border}`,
                  borderRadius: '6px', padding: '4px 10px', fontSize: '11px',
                  cursor: 'pointer', fontWeight: '600', fontFamily: 'system-ui, sans-serif',
                }}>
                <i className={`fa-solid fa-eye${showDiscontinued ? '' : '-slash'}`} style={{ fontSize: '10px' }} />
                {showDiscontinued ? 'Showing discontinued' : 'Discontinued hidden'}
              </button>
              <button
                onClick={() => setShowProductParts(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  background: showProductParts ? '#0f766e' : 'none',
                  color: showProductParts ? '#5eead4' : C.muted,
                  border: `1px solid ${showProductParts ? '#0d9488' : C.border}`,
                  borderRadius: '6px', padding: '4px 10px', fontSize: '11px',
                  cursor: 'pointer', fontWeight: '600', fontFamily: 'system-ui, sans-serif',
                }}>
                <i className={`fa-solid fa-eye${showProductParts ? '' : '-slash'}`} style={{ fontSize: '10px' }} />
                {showProductParts ? 'Showing parts in products' : 'Parts in products hidden'}
              </button>
            </div>
          </div>

          <div style={s.table}>
            <div style={s.tableHeader}>
              <span></span>
              <span>Part / Product</span>
              <span>Mould</span>
              <span>Machine</span>
              <span></span>
              <span>Material Recipe</span>
            </div>
            {(() => {
              const partProductCount = {};
              products.forEach(prod => {
                (prod.product_parts || []).forEach(pp => {
                  const pid = pp.part?.id;
                  if (pid) partProductCount[pid] = (partProductCount[pid] || 0) + 1;
                });
              });
              const activeParts    = showDiscontinued ? parts : parts.filter(p => p.status !== 'discontinued');
              const activeProducts = showDiscontinued ? products : products.filter(p => !p.discontinued);
              const visibleParts   = showProductParts
                ? activeParts
                : activeParts.filter(p => !partProductCount[p.id]);
              const q = search.toLowerCase();
              let items = [
                ...activeProducts.map(p => ({ type: 'product', data: p })),
                ...visibleParts.map(p => ({ type: 'part', data: p })),
              ].sort((a, b) => sortKey(a.type, a.data).toLowerCase().localeCompare(sortKey(b.type, b.data).toLowerCase()));

              if (search) {
                items = items.filter(item => {
                  if (item.type === 'product') {
                    const p = item.data;
                    return (p.name||'').toLowerCase().includes(q) || (p.code||'').toLowerCase().includes(q) ||
                      (p.product_parts||[]).some(pp =>
                        (pp.part?.name||'').toLowerCase().includes(q) || (pp.part?.part_number||'').toLowerCase().includes(q)
                      );
                  } else {
                    const p = item.data;
                    return (p.name||'').toLowerCase().includes(q) || (p.part_number||'').toLowerCase().includes(q) || (p.customer||'').toLowerCase().includes(q);
                  }
                });
              }

              if (items.length === 0) return (
                <div style={{ padding: '40px', textAlign: 'center', color: C.muted, fontSize: '13px' }}>
                  {search ? `No results for "${search}"` : 'No parts yet'}
                </div>
              );

              return items.map(item => {
                if (item.type === 'product') {
                  const product = item.data;
                  const isExpanded = expandedProducts.has(product.id);
                  return (
                    <React.Fragment key={`product-${product.id}`}>
                      <div style={{ ...s.tableRow, background: '#161b22', cursor: 'pointer' }}
                        onClick={() => toggleProduct(product.id)}
                        onMouseEnter={e => e.currentTarget.style.background = '#1a2030'}
                        onMouseLeave={e => e.currentTarget.style.background = '#161b22'}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <i className={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'}`} style={{ fontSize: '10px', color: C.teal }} />
                        </div>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <i className="fa-solid fa-layer-group" style={{ fontSize: '11px', color: product.discontinued ? '#b45309' : C.teal }} />
                            <span style={{ ...s.partName, color: product.discontinued ? '#fcd34d' : C.teal }}>{product.name}</span>
                            {product.discontinued && (
                              <span style={{ fontSize: '9px', background: '#78350f', color: '#fcd34d', border: '1px solid #b45309', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>
                                Discontinued
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                            <span style={s.partNum}>{product.code}</span>
                            <span style={{ fontSize: '10px', color: '#4b5563' }}>{product.product_parts?.length || 0} parts</span>
                          </div>
                        </div>
                        <div style={s.cell}>—</div>
                        <div style={s.cell}>—</div>
                        <div />
                        <div style={s.cell}>—</div>
                      </div>
                      {isExpanded && (product.product_parts || []).sort((a, b) => (a.part?.name || '').localeCompare(b.part?.name || '')).map(pp => (
                        <div key={pp.id} style={{ ...s.tableRow, background: '#0f1117', paddingLeft: '32px', cursor: 'pointer', borderLeft: '3px solid #14b8a620' }}
                          onClick={() => setSelected(pp.part?.id)}
                          onMouseEnter={e => e.currentTarget.style.background = '#141a24'}
                          onMouseLeave={e => e.currentTarget.style.background = '#0f1117'}>
                          <div />
                          <div>
                            <div style={s.partName}>{pp.part?.name}</div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '2px' }}>
                              <span style={s.partNum}>{pp.part?.part_number || '—'}</span>
                              <span style={{ fontSize: '10px', background: '#1e2329', border: `1px solid ${C.border}`, color: '#9ca3af', borderRadius: '4px', padding: '1px 6px' }}>×{pp.qty_per_assembly}</span>
                            </div>
                          </div>
                          <div style={s.cell}>{pp.part?.mould?.name || pp.part?.mould?.ref || '—'}</div>
                          <div style={s.cell}>{pp.part?.primary_machine?.display_name || pp.part?.primary_machine?.name || '—'}</div>
                          {(() => {
                            const partFull = parts.find(p => p.id === pp.part?.id) || {};
                            const hex = colours.find(c => c.name === partFull.colour)?.hex;
                            return (
                              <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  {partFull.colour && <div title={partFull.colour} style={{ width: '12px', height: '12px', borderRadius: '50%', background: hex || '#6b7280', border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }} />}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                                  <PartsRecipeSummaryCell part={partFull} onOpen={() => setRecipeModalPart(partFull)} />
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      ))}
                    </React.Fragment>
                  );
                } else {
                  const part = item.data;
                  return (
                    <div key={`part-${part.id}`} style={{ ...s.tableRow, cursor: 'pointer' }}
                      onClick={() => setSelected(part.id)}
                      onMouseEnter={e => e.currentTarget.style.background = '#242b35'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <div />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ ...s.partName, ...(part.status === 'discontinued' ? { color: '#fcd34d' } : {}) }}>{part.name}</span>
                          {part.status === 'discontinued' && (
                            <span style={{ fontSize: '9px', background: '#78350f', color: '#fcd34d', border: '1px solid #b45309', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>
                              Discontinued
                            </span>
                          )}
                          {partProductCount[part.id] > 0 && (
                            <span style={{ fontSize: '9px', background: '#0f766e', color: '#5eead4', border: '1px solid #0d9488', borderRadius: '4px', padding: '1px 5px', flexShrink: 0 }}>
                              {partProductCount[part.id]} product{partProductCount[part.id] > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                        <div style={s.partNum}>{part.part_number || '—'}</div>
                      </div>
                      <div style={s.cell}>{part.mould?.name || part.mould?.ref || '—'}</div>
                      <div style={s.cell}>{part.primary_machine?.display_name || part.primary_machine?.name || '—'}</div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {part.colour ? (() => {
                          const hex = colours.find(c => c.name === part.colour)?.hex;
                          return <div title={part.colour} style={{ width: '12px', height: '12px', borderRadius: '50%', background: hex || '#6b7280', border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }} />;
                        })() : null}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
                        <PartsRecipeSummaryCell part={part} onOpen={() => setRecipeModalPart(part)} />
                      </div>
                    </div>
                  );
                }
              });
            })()}
          </div>
        </>
      )}
      </div>)}
      {recipeModalPart && (
        <PartsRecipeModal part={recipeModalPart} onClose={() => setRecipeModalPart(null)} />
      )}
    </div>
  );
}

function PartsRecipeSummaryCell({ part, onOpen }) {
  const prs = part.part_recipes || [];
  if (prs.length === 0) return <span style={{ fontSize: '12px', color: '#4b5563' }}>—</span>;
  if (prs.length > 1) return (
    <button onClick={onOpen} style={{ background: 'none', border: 'none', color: C.teal, fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'system-ui, sans-serif', fontWeight: '600' }}>
      Multiple recipes
    </button>
  );
  const recipe = prs[0].recipe;
  const comps = recipe?.recipe_components || [];
  if (comps.length === 0) return <span style={{ fontSize: '12px', color: '#4b5563' }}>—</span>;
  if (comps.length <= 3) return (
    <span style={{ fontSize: '11px', color: '#9ca3af', lineHeight: '1.4' }}>
      {comps.map((c, i) => (
        <span key={i}>{i > 0 ? ' · ' : ''}<strong style={{ color: '#f0f2f5' }}>{c.percentage}%</strong> {c.material?.name}</span>
      ))}
    </span>
  );
  return (
    <button onClick={onOpen} style={{ background: 'none', border: 'none', color: C.teal, fontSize: '12px', cursor: 'pointer', padding: 0, fontFamily: 'system-ui, sans-serif', fontWeight: '600', textAlign: 'left', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {recipe?.name || 'View recipe'}
    </button>
  );
}

function PartsRecipeModal({ part, onClose }) {
  const prs = part.part_recipes || [];
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', width: '100%', maxWidth: '440px', boxShadow: '0 24px 48px rgba(0,0,0,0.5)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #2a3040' }}>
          <div style={{ fontSize: '14px', fontWeight: '700' }}>Recipes — {part.name}</div>
          <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer', padding: '2px 6px' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '16px 20px', maxHeight: '70vh', overflowY: 'auto' }}>
          {prs.map(pr => {
            const recipe = pr.recipe;
            const comps = recipe?.recipe_components || [];
            return (
              <div key={pr.id} style={{ marginBottom: '16px', paddingBottom: '16px', borderBottom: '1px solid #2a3040' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '700' }}>{recipe?.name}</span>
                  {pr.is_primary && <span style={{ fontSize: '9px', background: C.teal, color: '#fff', padding: '1px 5px', borderRadius: '3px' }}>Primary</span>}
                </div>
                {recipe?.material && <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '6px' }}>{recipe.material.name}</div>}
                {comps.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: i < comps.length - 1 ? '1px solid #1a1f2a' : 'none' }}>
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>{c.material?.name}</span>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: '#f0f2f5' }}>{c.percentage}%</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const COLS = '36px 2fr 1fr 1fr 28px 1.5fr';
const s = {
  page:        cs.page,
  header:      cs.pageHeader,
  title:       cs.pageTitle,
  sub:         cs.pageSub,
  addBtn:      cs.btnPrimary,
  center:      cs.center,
  table:       cs.tableWrap,
  tableHeader: { ...cs.tableHeader, gridTemplateColumns: COLS, minWidth: '480px' },
  tableRow:    { ...cs.tableRow,    gridTemplateColumns: COLS, minWidth: '480px' },
  partName:    { fontSize: '13px', fontWeight: '700' },
  partNum:     { fontSize: '10px', color: C.teal, fontFamily: 'monospace', marginTop: '2px' },
  cell:        { fontSize: '12px', color: '#9ca3af' },
  searchWrap:  { position: 'relative', display: 'flex', alignItems: 'center' },
  searchIcon:  { position: 'absolute', left: '12px', color: C.mutedDark, fontSize: '13px', pointerEvents: 'none', zIndex: 1 },
  searchInput: { width: '100%', background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', color: C.text, padding: '10px 36px', fontSize: '13px', boxSizing: 'border-box', fontFamily: 'system-ui, sans-serif' },
  clearBtn:    { position: 'absolute', right: '10px', background: 'none', border: 'none', color: C.muted, cursor: 'pointer', fontSize: '13px', padding: '2px 4px' },
  suggestions: { position: 'absolute', top: '100%', left: 0, right: 0, background: C.surface, border: `1px solid ${C.border}`, borderRadius: '8px', zIndex: 20, marginTop: '4px', maxHeight: '300px', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' },
  suggItem:    { display: 'flex', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${C.border}` },
};
